// Importing necessary modules and types
import { FuseThemesType } from '@fuse/core/FuseSettings/FuseSettings';

/**
 * The lightPaletteText object defines the text color palette for the light theme.
 */
export const lightPaletteText = {
	primary: 'rgb(17, 24, 39)',
	secondary: 'rgb(107, 114, 128)',
	disabled: 'rgb(149, 156, 169)'
};

/**
 * The darkPaletteText object defines the text color palette for the dark theme.
 */
export const darkPaletteText = {
	primary: 'rgb(255,255,255)',
	secondary: 'rgb(148, 163, 184)',
	disabled: 'rgb(156, 163, 175)'
};

/**
 * Utility function to retrieve the value of a CSS variable.
 * @param variableName - The name of the CSS variable to retrieve.
 * @returns the hex value of the CSS variable.
 */
export function getCSSVariable(variableName: string): string {
	return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
}

/**
 * The themesConfig object is a configuration object for the color themes of the Fuse application.
 */
export const themesConfig: FuseThemesType = {
	default: {
		palette: {
			mode: 'light',
			divider: 'rgba(214,214,214,0.7)',
			text: {
				primary: '#212121',
				secondary: '#5F6368'
			},
			common: {
				black: '#000000',
				white: '#FFFFFF'
			},
			primary: {
				light: getCSSVariable('--secondary-light'),
				main: getCSSVariable('--primary-light'),
        dark: getCSSVariable('--secondary-light'),
        nuanceLight: getCSSVariable('--primary-nuance-light'),
				nuanceLighter: getCSSVariable('--primary-nuance-lighter'),
				nuanceDarker: getCSSVariable('--primary-nuance-darker'),
				nuanceDark: getCSSVariable('--primary-nuance-dark'),
				contrastText: getCSSVariable('--button-text') || '#FFFFFF'
			},
			secondary: {
				light: getCSSVariable('--secondary-light'),
				main: getCSSVariable('--secondary-light'),
				dark: getCSSVariable('--secondary-light'),
				contrastText: getCSSVariable('--button-text') || '#FFFFFF'
			},
			background: {
				paper: '#F4F4F4',
				default: '#E8E8E8'
			},
			error: {
				light: getCSSVariable('--danger-light'),
				main: getCSSVariable('--danger-light'),
				dark: getCSSVariable('--danger-dark'),
				contrastText: '#FFFFFF'
			},
			warning: {
				extraLight: getCSSVariable('--warning-extra-light'),
				light: getCSSVariable('--warning-light'),
				main: getCSSVariable('--warning-light'),
				dark: getCSSVariable('--warning-dark'),
				contrastText: '#000000',
			},
			info: {
				extraLight: getCSSVariable('--info-extra-light'),
				light: getCSSVariable('--info-light'),
				main: getCSSVariable('--info-light'),
				dark: getCSSVariable('--info-dark'),
				contrastText: '#000000',
			},
		}
	},
	defaultDark: {
		palette: {
			mode: 'dark',
			divider: 'rgba(79,79,79,0.5)',
			text: {
				primary: '#E0E0E0',
				secondary: '#B0BEC5'
			},
			common: {
				black: '#000000',
				white: '#FFFFFF'
			},
			primary: {
				light: getCSSVariable('--primary-light'),
				main: getCSSVariable('--primary-dark'),
				dark: getCSSVariable('--primary-dark'),
				nuanceLight: getCSSVariable('--primary-nuance-light'),
				nuanceLighter: getCSSVariable('--primary-nuance-lighter'),
				nuanceDarker: getCSSVariable('--primary-nuance-darker'),
				nuanceDark: getCSSVariable('--primary-nuance-dark'),
				contrastText: getCSSVariable('--button-text-dark') || '#FFFFFF'
			},
			secondary: {
				light: getCSSVariable('--secondary-light'),
				main: getCSSVariable('--secondary-dark'),
				dark: getCSSVariable('--secondary-dark'),
				contrastText: getCSSVariable('--button-text-dark') || '#FFFFFF'
			},
			background: {
				paper: '#1E1E1E',
				default: '#121212'
			},
			error: {
				light: getCSSVariable('--danger-light'),
				main: getCSSVariable('--danger-dark'),
				dark: getCSSVariable('--danger-dark'),
				contrastText: '#FFFFFF'
			},
			warning: {
				extraLight: getCSSVariable('--warning-extra-light'),
				light: getCSSVariable('--warning-light'),
				main: getCSSVariable('--warning-dark'),
				dark: getCSSVariable('--warning-dark'),
				contrastText: '#FFFFFF',
			},
			info: {
				extraLight: getCSSVariable('--info-extra-light'),
				light: getCSSVariable('--info-light'),
				main: getCSSVariable('--info-light'),
				dark: getCSSVariable('--info-dark'),
				contrastText: '#FFFFFF',
			},
		}
	},
};

export default themesConfig;
